import React, { useEffect, useRef, useState } from 'react';

/**
 * Wraps children with a scroll-triggered animation.
 * Animates text/elements into view when they enter the viewport.
 * 
 * Props:
 * - animation: 'fade-up' | 'fade-left' | 'fade-right' | 'scale' | 'text-reveal'
 * - delay: animation delay in ms
 * - duration: animation duration in ms (default 800)
 * - threshold: how much of element must be visible (0-1, default 0.15)
 * - once: only animate once (default true)
 */
export const ScrollReveal = ({ 
  children, 
  animation = 'fade-up', 
  delay = 0, 
  duration = 800,
  threshold = 0.15,
  once = true,
  className = '',
  as: Component = 'div'
}) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          if (once) observer.unobserve(entry.target);
        } else if (!once) {
          setIsVisible(false);
        }
      },
      { threshold, rootMargin: '0px 0px -50px 0px' }
    );

    const currentRef = ref.current;
    if (currentRef) observer.observe(currentRef);

    return () => {
      if (currentRef) observer.unobserve(currentRef);
    };
  }, [threshold, once]);

  const getTransform = () => {
    if (isVisible) return 'translate3d(0, 0, 0) scale(1)';
    switch (animation) {
      case 'fade-up': return 'translate3d(0, 40px, 0)';
      case 'fade-down': return 'translate3d(0, -40px, 0)';
      case 'fade-left': return 'translate3d(-40px, 0, 0)';
      case 'fade-right': return 'translate3d(40px, 0, 0)';
      case 'scale': return 'scale(0.92)';
      case 'text-reveal': return 'translate3d(0, 20px, 0)';
      default: return 'translate3d(0, 40px, 0)';
    }
  };

  const style = {
    opacity: isVisible ? 1 : 0,
    transform: getTransform(),
    transition: `opacity ${duration}ms cubic-bezier(0.25, 0.46, 0.45, 0.94) ${delay}ms, transform ${duration}ms cubic-bezier(0.25, 0.46, 0.45, 0.94) ${delay}ms`,
    filter: animation === 'text-reveal' && !isVisible ? 'blur(4px)' : 'blur(0)',
    willChange: 'opacity, transform',
  };

  return (
    <Component ref={ref} style={style} className={className}>
      {children}
    </Component>
  );
};
